import { AfterContentInit, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Meta } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { fromEvent } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { MFE_AGENT_UI } from '../micro-frontends/mfe-agent-ui/config/mfe-agent-ui.config';
import { UserService } from './angie-shared/services/user.service';
import { StateService } from './core';
import { InsightsService } from './modules/insights/services/insights.service';

/**
 * List of urls where tracking must be disabled.
 * e.g. Authorization pages
 */
const SKIP_TRACKING: RegExp[] = [
	/^\/users\/sign_in/,
	/^\/users\/auth/,
	/^\/users\/sign_up/,
	/^\/users\/confirmation/,
	/^\/users\/code_redeem/,
	/^\/accept_invitation/,
	/^\/users\/password/,
	/^\/users\/privacy-policy/
];

/**
 * App Component - our main bootstrap component.
 * This component has been initialized first and all the other components will get loaded from here.
 * This one is used inside our index.html.erb where we define our root level tag (this).
 */
@Component({
	selector: 'angie-app',
	templateUrl: './app.component.html',
	standalone: false
})
export class AppComponent implements AfterContentInit, OnInit {
	/**
	 * Constructor
	 */
	constructor(
		private readonly meta: Meta,
		private readonly stateService: StateService,
		private readonly userService: UserService,
		private readonly router: Router,
		private readonly insightsService: InsightsService,
		private readonly translate: TranslateService
	) {}

	private readonly destroyRef = inject(DestroyRef);

	isLearner = this.userService.isLearner();
	isLoggedIn = this.stateService.luUser;
	isInLearnerView = this.stateService.isInLearnerView();
	ai_assistant = this.stateService.portalMeta?.ai_assistant; // from luphub
	ai_assistant_enabled = this.stateService.portalMeta?.ai_assistant_enabled; // from settings page
	aiSmartSearchEnabled =
		this.ai_assistant && this.ai_assistant_enabled && (this.isLearner || this.isInLearnerView) && this.isLoggedIn; // all conditions to show ai smart search
	isAgentUIEnabled = this.stateService.portalMeta?.lia_agent_ui; // switches between the new agent UI and the legacy smart search
	// The admin agent is the same <mfe-agent-ui> mounted floating on admin pages,
	// on the opposite side of the learner-view split. The learner flags don't apply.
	// Shown only to the roles ai-app admits: admins and managers. ai-app's own
	// check stays the real gate.
	adminAgentEnabled =
		this.stateService.portalMeta?.lia_admin_agent &&
		this.isLoggedIn &&
		!this.isInLearnerView &&
		(this.userService.isAdmin() || this.userService.isManager());
	// Pages that declare `data: { hasDockedAgent: true }` on their route mount
	// their own docked agent panel; the floating admin mount stands down there.
	hasDockedAgent = toSignal(
		this.router.events.pipe(
			filter(event => event instanceof NavigationEnd),
			map(() => routeDeclaresDockedAgent(this.router.routerState.snapshot.root))
		),
		{ initialValue: false }
	);

	agentUiConfig = {
		modes: ['floating', 'full-screen'],
		hideBubbleInFloatingMode: false,
		courseImageHost: this.stateService.portalMeta?.as3_main_bucket_s3_host,
		localization: {
			'mfe_agent_ui.header.title': this.translate.instant('mfe_agent_ui.default.header.title'),
			'mfe_agent_ui.header.subtitle': this.translate.instant('mfe_agent_ui.default.header.subtitle'),
			'mfe_agent_ui.bubble.aria_label': this.translate.instant('mfe_agent_ui.default.bubble.aria_label'),
			'mfe_agent_ui.composer.placeholder': this.translate.instant('mfe_agent_ui.default.composer.placeholder'),
			'mfe_agent_ui.reasoning.working': this.translate.instant('mfe_agent_ui.default.reasoning.working')
		}
	};
	// Host config for <mfe-agent-ui>, serialised because the MFE is a lazily-defined
	// custom element and reads `config` as a JSON attribute. Restricts the panel to
	// floating and full-screen (dropping sidebar — there is nothing to dock into,
	// and a persisted sidebar preference would otherwise render a full-width slab)
	// and keeps the floating action bubble visible so a collapsed panel can be
	// re-opened.
	// `sessionScope` namespaces the persisted session (chat guid, window mode,
	// open/collapsed) in browser storage: `lia.l` for the learner surface,
	// `lia.a` for the admin floating mount, so the conversation survives the
	// full-page loads admin navigation triggers. The journey editor's docked
	// panel deliberately has no scope — its chat stays per-page-load, and one
	// conversation never mixes the general and journey personas across mounts.
	learnerAgentUiConfig = JSON.stringify({
		...this.agentUiConfig,
		streamEndpoint: 'ai-assistant/learner',
		sessionScope: 'lia.l'
	});
	// Admin mount: same shape restrictions, rebranded via the localization
	// override channel — the panel title, bubble tooltip, and composer
	// placeholder are the whole of the difference from the journey agent.
	// `streamEndpoint` is explicit because DEFAULTS.streamEndpoint now points at
	// the journey agent's deprecated alias (ai-app CET-1860's admin/journey
	// split) — the general persona has its own dedicated route.
	adminAgentUiConfig = JSON.stringify({
		...this.agentUiConfig,
		sessionScope: 'lia.a',
		streamEndpoint: 'ai-assistant/admin'
	});

	ngOnInit(): void {
		this.router.events
			.pipe(
				filter((event): event is NavigationEnd => event instanceof NavigationEnd),
				filter((event): event is NavigationEnd => SKIP_TRACKING.findIndex(it => it.test(event.url)) < 0)
			)
			.subscribe(event => this.insightsService.pageViewEvent(event));

		this.listenForAgentCourseCardClicks();

		// Initialize Pendo analytics
		if (typeof initPendo === 'function') {
			initPendo();
		}
	}

	/**
	 * <mfe-agent-ui> runs its own router, so a course card clicked inside it is
	 * announced on `window`; route to that course's details page here.
	 */
	private listenForAgentCourseCardClicks(): void {
		fromEvent<CustomEvent<{ course_id?: string | number }>>(window, MFE_AGENT_UI.courseCardClickEvent)
			.pipe(
				map(event => event.detail?.course_id),
				filter(courseId => courseId !== undefined && courseId !== null && courseId !== ''),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(courseId =>
				this.router.navigate(['/content-details', courseId, 0], {
					queryParams: { caller_name: 'lia' }
				})
			);
	}

	/**
	 * After View Init LifeCycle hook
	 */
	ngAfterContentInit(): void {
		this.setXSRFToken();
	}

	/**
	 * Method to get called after content has been inited
	 * We're going to set xsrfToken token here which we'll read from meta csrf-token and set it into state service
	 */
	private setXSRFToken(): void {
		this.stateService.xsrfToken = this.meta.getTag('name="csrf-token"').content;
	}
}

function routeDeclaresDockedAgent(root: ActivatedRouteSnapshot): boolean {
	for (let node: ActivatedRouteSnapshot | null = root; node; node = node.firstChild) {
		if (node.data?.['hasDockedAgent'] === true) {
			return true;
		}
	}
	return false;
}

// Declare the global initPendo function
declare function initPendo(): void;
